import { useCallback, useEffect, useMemo, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { useGlobalAPIQueryContext } from "@dev2_techwalnut/tw-api-query-hook";
import Swal from "sweetalert2";
import LiveSiteConfiguration from "./LiveSiteConfiguration";
import LiveSiteMap from "./LiveSiteMap";
import LiveSitePipelineStages from "./LiveSitePipelineStages";
import "./LiveSite.css";

const unwrap = (response) =>
    response?.data?.results?.data ?? response?.data?.data ?? response?.data;

const formatTs = (value) => {
    if (!value) return "Never";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
};

const LiveSite = () => {
    const { apiClient } = useGlobalAPIQueryContext();
    const privileges = secureLocalStorage.getItem("privileges") || [];
    const canUpdate = privileges.includes("live_site_update");
    const [mode, setMode] = useState("view");
    const [section, setSection] = useState("mapping");
    const [syncMeta, setSyncMeta] = useState(null);
    const [resyncingAll, setResyncingAll] = useState(false);
    const [syncTick, setSyncTick] = useState(0);
    const canEdit = mode === "update" && canUpdate;

    const visibleMode = useMemo(() => {
        if (mode === "update" && !canUpdate) return "view";
        return mode;
    }, [mode, canUpdate]);

    const visibleSection =
        visibleMode === "view" && section === "pipeline" ? "mapping" : section;

    const loadSyncMeta = useCallback(async () => {
        const response = await apiClient.get("/api/live-site/elorca-heartbeat/");
        const data = unwrap(response) || null;
        setSyncMeta(data);
    }, [apiClient]);

    useEffect(() => {
        loadSyncMeta().catch(() => setSyncMeta(null));
    }, [loadSyncMeta]);

    const resyncAll = async () => {
        const confirm = await Swal.fire({
            title: "Resync all machines?",
            text: "This pulls the latest Elorca heartbeat, flavour, and online status for every node.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Resync all",
        });
        if (!confirm.isConfirmed) return;
        setResyncingAll(true);
        try {
            const response = await apiClient.post("/api/live-site/elorca-heartbeat/", {});
            const data = unwrap(response) || null;
            setSyncMeta(data);
            setSyncTick((value) => value + 1);
            Swal.fire({
                icon: "success",
                title: "Resync complete",
                timer: 1600,
                showConfirmButton: false,
            });
        } catch (error) {
            Swal.fire(
                "Resync failed",
                error?.response?.data?.results?.message || error.message,
                "error"
            );
        } finally {
            setResyncingAll(false);
        }
    };

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content">
                        <h4 className="nk-block-title">Live Site</h4>
                        <p className="text-soft">
                            {visibleMode === "view"
                                ? "View the plant map, machine status, and commissioning checks."
                                : "Edit floors, areas, machine placement, pipeline stages, and Sorted/Implemented checks."}
                        </p>
                        <p className="text-soft mb-0">
                            Last synced with Elorca: {formatTs(syncMeta?.last_pulled_at)}
                            {syncMeta?.last_error ? ` · Last error: ${syncMeta.last_error}` : ""}
                        </p>
                    </div>
                    {canUpdate ? (
                        <div className="nk-block-head-content">
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={resyncAll}
                                disabled={resyncingAll}
                            >
                                {resyncingAll ? "Resyncing…" : "Resync all"}
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>

            <ul className="nav nav-tabs live-site-tabs">
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${visibleMode === "view" ? "active" : ""}`}
                        onClick={() => setMode("view")}
                    >
                        View
                    </button>
                </li>
                {canUpdate ? (
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${visibleMode === "update" ? "active" : ""}`}
                            onClick={() => setMode("update")}
                        >
                            Update
                        </button>
                    </li>
                ) : null}
            </ul>

            <ul className="nav nav-sm nav-tabs live-site-subtabs">
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${visibleSection === "mapping" ? "active" : ""}`}
                        onClick={() => setSection("mapping")}
                    >
                        Site mapping
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${visibleSection === "configuration" ? "active" : ""}`}
                        onClick={() => setSection("configuration")}
                    >
                        Configuration
                    </button>
                </li>
                {visibleMode === "update" ? (
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${visibleSection === "pipeline" ? "active" : ""}`}
                            onClick={() => setSection("pipeline")}
                        >
                            Pipeline stages
                        </button>
                    </li>
                ) : null}
            </ul>

            {visibleSection === "mapping" ? (
                <LiveSiteMap canEdit={canEdit} canResync={canUpdate} onResynced={loadSyncMeta} syncTick={syncTick} />
            ) : visibleSection === "pipeline" ? (
                <LiveSitePipelineStages />
            ) : (
                <LiveSiteConfiguration canEdit={canEdit} />
            )}
        </>
    );
};

export default LiveSite;
